import { useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';

import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';

interface SessionRef {
    ID: number;
}

export interface BookMeetingAvailability {
    /** Index of the session whose availability is currently loaded/shown. */
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    availability: BookingAvailabilityApiResponse['availability'];
    loadError: BookingAvailabilityApiResponse['error'];
    isLoading: boolean;
    serverTimezone: string;
}

function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Pacific/Auckland';
    }
}

/**
 * Loads booking availability for the project's bookable sessions. Lives in the
 * always-mounted accordion container (not the collapsed row body) so the first
 * session's slots are fetched eagerly on page load — the calendar is ready the
 * moment the (03) "Book a Meeting" row opens, instead of starting from a
 * spinner. Switching sessions refetches for the newly selected one. Safe to
 * call with an empty `sessions` array (no request fires). See BC-4603.
 */
export const useBookMeetingAvailability = (
    sessions: SessionRef[],
): BookMeetingAvailability => {
    const [activeIndex, setActiveIndex] = useState(0);
    const sessionId = sessions[activeIndex]?.ID ?? sessions[0]?.ID;

    const serverTimezone = getBrowserTimezone();
    const availabilityFetcher = useFetcher<BookingAvailabilityApiResponse>();

    // Reset fetcher data and reload when switching sessions.
    const prevSessionIdRef = useRef<number | undefined>(undefined);

    // Load availability on mount (page load) and whenever the session changes.
    useEffect(() => {
        if (!sessionId) return;
        if (
            prevSessionIdRef.current === sessionId &&
            availabilityFetcher.data
        ) {
            return;
        }
        prevSessionIdRef.current = sessionId;
        // singleTimezone: the inline card has no timezone selector, so request
        // one timezone's slots instead of all ~11 (the full grid is ~300KB).
        availabilityFetcher.load(
            `${BOOKING_AVAILABILITY_API_ROUTE}?sessionId=${sessionId}&timezone=${encodeURIComponent(serverTimezone)}&singleTimezone=1`,
        );
        // availabilityFetcher intentionally omitted: its state changes must not
        // re-trigger the load or every in-flight response cancels the previous request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, serverTimezone]);

    const availability = availabilityFetcher.data?.availability;
    const loadError = availabilityFetcher.data?.error;
    const isLoading =
        availabilityFetcher.state !== 'idle' || (!availability && !loadError);

    return {
        activeIndex,
        setActiveIndex,
        availability,
        loadError,
        isLoading,
        serverTimezone,
    };
};
